import axios from "axios";
import type {
  AdminUser,
  Catalog,
  Playlist,
  SeriesDetail,
  ShortEpgEntry,
  UserMe,
} from "./types";

export const api = axios.create({
  baseURL: "/",
  withCredentials: true,
  // Catalog responses can be large — give them room to breathe.
  timeout: 120_000,
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !location.pathname.startsWith("/login")) {
      // Auth lost — bounce to login. Use replace so back-button doesn't loop.
      const next = encodeURIComponent(location.pathname + location.search);
      location.replace(`/login?next=${next}`);
    }
    return Promise.reject(err);
  }
);

// ─── auth ──────────────────────────────────────────────────────────────
export const Auth = {
  me: () => api.get<UserMe>("/api/auth/me").then((r) => r.data),
  login: (username: string, password: string) =>
    api.post<UserMe>("/api/auth/login", { username, password }).then((r) => r.data),
  logout: () => api.post("/api/auth/logout").then((r) => r.data),
  changePassword: (current_password: string, new_password: string) =>
    api
      .post("/api/auth/change-password", { current_password, new_password })
      .then((r) => r.data),
};

// ─── playlists ─────────────────────────────────────────────────────────
export const Playlists = {
  list: () => api.get<Playlist[]>("/api/playlists").then((r) => r.data),
  create: (body: {
    name: string;
    kind: "xtream" | "m3u";
    xtream_url?: string;
    xtream_username?: string;
    xtream_password?: string;
    m3u_url?: string;
  }) => api.post<Playlist>("/api/playlists", body).then((r) => r.data),
  rename: (id: number, name: string) =>
    api.patch<Playlist>(`/api/playlists/${id}`, { name }).then((r) => r.data),
  update: (
    id: number,
    body: {
      name?: string;
      xtream_url?: string;
      xtream_username?: string;
      xtream_password?: string;
      m3u_url?: string;
    }
  ) => api.put<Playlist>(`/api/playlists/${id}`, body).then((r) => r.data),
  delete: (id: number) => api.delete(`/api/playlists/${id}`).then((r) => r.data),
  catalog: (id: number, onProgress?: (loaded: number, total: number) => void) =>
    api
      .get<Catalog>(`/api/playlists/${id}/catalog`, {
        onDownloadProgress: (e) => {
          if (onProgress && e.total) onProgress(e.loaded, e.total);
          else if (onProgress) onProgress(e.loaded, 0);
        },
      })
      .then((r) => r.data),
  seriesDetail: (id: number, seriesId: string) =>
    api.get<SeriesDetail>(`/api/playlists/${id}/series/${seriesId}`).then((r) => r.data),
  shortEpg: (id: number, streamId: number, limit = 4) =>
    api
      .get<{ epg_listings: ShortEpgEntry[] }>(
        `/api/playlists/${id}/epg/short/${streamId}`,
        { params: { limit } }
      )
      .then((r) => r.data),
  /** Just the programme airing right now — ~550 bytes vs ~127 KB for fullEpg.
   *  Used by the phone channel list, one call per visible row. */
  nowPlaying: (id: number, streamId: number) =>
    api
      .get<{ now: ShortEpgEntry | null; pct: number; ends_in_s: number }>(
        `/api/playlists/${id}/epg/now/${streamId}`
      )
      .then((r) => r.data),

  /** Programmes overlapping the next `hours` — the guide grid's data source.
   *  Sliced server-side from the same cached full EPG. */
  epgWindow: (id: number, streamId: number, hours = 4) =>
    api
      .get<{ window_start: number; window_end: number; programmes: { title?: string; start: number; stop: number }[] }>(
        `/api/playlists/${id}/epg/window/${streamId}`,
        { params: { hours } }
      )
      .then((r) => r.data),

  fullEpg: (id: number, streamId: number) =>
    api
      .get<{ epg_listings: ShortEpgEntry[] }>(
        `/api/playlists/${id}/epg/full/${streamId}`
      )
      .then((r) => r.data),
  catchupUrl: (id: number, streamId: number, startMs: number, durationMin: number) =>
    api
      .get<{ url: string; proxy_url: string; panel_start: string }>(
        `/api/playlists/${id}/catchup-url`,
        { params: { stream_id: streamId, start_ms: startMs, duration: durationMin } }
      )
      .then((r) => r.data),
  info: (id: number) =>
    api
      .get<PlaylistInfoOut>(`/api/playlists/${id}/info`)
      .then((r) => r.data),
};

export interface PlaylistInfoOut {
  exp_date?: string | number | null;
  status?: string | null;
  max_connections?: string | number | null;
  active_cons?: string | number | null;
}

export const Cast = {
  token: (playlistId: number) =>
    api.post<{ token: string }>("/api/cast/token", { playlist_id: playlistId }).then((r) => r.data.token),
};

export interface SubtitleTrack {
  index: number;
  lang: string;
  title: string;
  codec: string;
  default: boolean;
  forced: boolean;
}

export interface TransmuxInfo {
  duration_s: number;
  video_codec: string | null;
  audio_codec: string | null;
  needs_video_transcode: boolean;
  needs_audio_transcode: boolean;
  subtitles: SubtitleTrack[];
}

export const Transmux = {
  movieInfo: (pid: number, streamId: number, ext: string) =>
    api.get<TransmuxInfo>(
      `/api/playlists/${pid}/transmux/movie/${streamId}/${encodeURIComponent(ext)}.info`
    ).then((r) => r.data),
  episodeInfo: (pid: number, episodeId: number, ext: string) =>
    api.get<TransmuxInfo>(
      `/api/playlists/${pid}/transmux/series/${episodeId}/${encodeURIComponent(ext)}.info`
    ).then((r) => r.data),
};

export interface VodInfo {
  info?: {
    tmdb_id?: number | string;
    imdb_id?: string;
    name?: string;
    plot?: string;
    duration_secs?: number;
  };
  movie_data?: { stream_id?: number; name?: string };
}

export const Vod = {
  info: (pid: number, streamId: number) =>
    api.get<VodInfo>(`/api/playlists/${pid}/vod-info/${streamId}`).then((r) => r.data),
};

export interface OpenSubtitleResult {
  file_id: number;
  language: string;
  release: string;
  downloads: number;
  rating: number;
  fps: number | null;
  hd: boolean;
  ai_translated: boolean;
  machine_translated: boolean;
  from_trusted: boolean;
  uploader: string;
  feature_type: string;
}

export const Subs = {
  configured: () =>
    api.get<{ ok: boolean; vip: boolean; user_agent: string }>("/api/subs/configured").then((r) => r.data),
  search: (params: {
    tmdb_id?: number;
    imdb_id?: string;
    query?: string;
    languages?: string;
    season?: number;
    episode?: number;
  }) => api.get<{ results: OpenSubtitleResult[] }>("/api/subs/search", { params }).then((r) => r.data),
  // Returns the URL — not the body — so we can hand it straight to a <track>.
  url: (file_id: number) => `/api/subs/download/${file_id}.vtt`,
};

// ─── admin ─────────────────────────────────────────────────────────────
export const Admin = {
  users: () => api.get<AdminUser[]>("/api/admin/users").then((r) => r.data),
  createUser: (username: string, isAdmin: boolean, initial_password?: string) =>
    api
      .post<{
        id: number;
        username: string;
        is_admin: boolean;
        initial_password: string;
      }>("/api/admin/users", { username, is_admin: isAdmin, initial_password })
      .then((r) => r.data),
  deleteUser: (id: number) => api.delete(`/api/admin/users/${id}`).then((r) => r.data),
  resetPassword: (id: number) =>
    api
      .post<{ username: string; new_password: string }>(
        `/api/admin/users/${id}/reset-password`
      )
      .then((r) => r.data),
  toggleDisabled: (id: number) =>
    api
      .post<{ id: number; disabled: boolean }>(`/api/admin/users/${id}/toggle-disabled`)
      .then((r) => r.data),
  activity: (limit = 200) =>
    api
      .get<
        Array<{
          id: number;
          ts: string;
          user_id: number | null;
          username: string | null;
          action: string;
          detail: string | null;
          ip: string | null;
        }>
      >(`/api/admin/activity`, { params: { limit } })
      .then((r) => r.data),

  // TV-app auto-update releases.
  releases: () =>
    api
      .get<
        Array<{
          version_code: number;
          version_name: string;
          size_bytes: number;
          uploaded_at: string;
          notes: string | null;
        }>
      >(`/api/admin/app/releases`)
      .then((r) => r.data),

  uploadRelease: (
    apk: File,
    versionCode: number,
    versionName: string,
    notes: string,
    onProgress?: (pct: number) => void,
  ) => {
    const fd = new FormData();
    fd.append("apk", apk);
    fd.append("version_code", String(versionCode));
    fd.append("version_name", versionName);
    fd.append("notes", notes);
    return api
      .post<{
        ok: boolean;
        version_code: number;
        version_name: string;
        size_bytes: number;
        sha256: string;
      }>(`/api/admin/app/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      })
      .then((r) => r.data);
  },

  deleteRelease: (versionCode: number) =>
    api.delete(`/api/admin/app/releases/${versionCode}`).then((r) => r.data),
};

// ─── public app-update endpoints (TV APK download) ─────────────────────
export interface AppLatestResponse {
  version_code: number;
  version_name: string;
  size_bytes: number;
  sha256: string;
  notes: string;
  uploaded_at: string;
  download_url: string;
}

export interface DesktopLatestResponse {
  version: string;
  size_bytes: number;
  sha256: string;
  notes: string;
  uploaded_at: string;
  download_url: string;
}

export const App = {
  latest: () => api.get<AppLatestResponse>("/api/app/latest").then((r) => r.data),
  desktopLatest: () =>
    api.get<DesktopLatestResponse>("/api/app/desktop/latest").then((r) => r.data),
};
